import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import { injectIntl } from 'react-intl';
import { TreeTypes } from 'hcl-web-editor/app/components/Model/SingleHCLModelView/SingleTreeView/Tools/TreeDictionaries';

import ApiManager from 'hcla-web-frontend-primitives/app/Api/ApiManager';
import TransferButton from 'hcla-web-frontend-primitives/app/components/primitives/Buttons/TransferButton';
import TypedConfirmationDialog from 'hcla-web-frontend-primitives/app/components/primitives/Dialogs/TypedConfirmationDialog';
import RemoveButton from 'hcla-web-frontend-primitives/app/components/primitives/Buttons/RemoveButton';

const LAYOUT_LABELS = {
  [TreeTypes.EVENT_TREE]: 'Event Sequences',
  [TreeTypes.FAULT_TREE]: 'Fault Trees',
  [TreeTypes.BAYESIAN_NETWORK]: 'Bayesian Networks',
};

const LAYOUT_TREE_TYPES = [TreeTypes.EVENT_TREE, TreeTypes.FAULT_TREE, TreeTypes.BAYESIAN_NETWORK];

const LayoutSwitch = withStyles(theme => ({
  switchBase: {
    '&$checked': { color: theme.palette.primary.main },
    '&$checked + $track': { backgroundColor: theme.palette.primary.main },
  },
  checked: {},
  track: {},
}))(Switch);

const readLayoutConfig = () => {
  const config = {};
  LAYOUT_TREE_TYPES.forEach((t) => {
    const stored = localStorage.getItem(LAYOUT_LABELS[t]);
    config[t] = stored === null ? true : stored === 'true';
  });
  return config;
};

const styles = theme => ({
  root: {
    flexGrow: 1,
  },
  transferColor: {
    color: '#ee9838',
  },
  removeColor: {
    color: '#ca4830',
  },
  layoutColor: {
    color: theme.palette.primary.main,
  },
  inCardContainer: {
    border: '1px solid #ccc',
    borderRadius: theme.spacing(1) / 2,
    padding: theme.spacing(1) * 2,
  },
});

class ModelSettingsAdvanced extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      details: props.details,
      openTypedConfirmationDialog: false,
      redirect: false,
      layoutConfig: readLayoutConfig(),
    };
  }

  handleToggle = (e, id) => {
    this.setState({
      [id]: !this.state[id],
    });
  };

  handleLayoutChange = (treeType) => {
    const label = LAYOUT_LABELS[treeType];
    const newValue = !this.state.layoutConfig[treeType];
    localStorage.setItem(label, String(newValue));
    this.setState(prev => ({
      layoutConfig: { ...prev.layoutConfig, [treeType]: newValue },
    }));
  };

  handleOnConfirmed = () => {
    const { details: { id } } = this.props;
    ApiManager.deleteModel(id)
      .then((response) => {
        if (response.status === 204) {
          this.handleToggle(null, 'redirect');
        }
      });
  };

  render() {
    const { classes, intl, details: { title } } = this.props;
    const { openTypedConfirmationDialog, redirect, layoutConfig } = this.state;
    if (redirect) {
      return <Redirect to="/" />;
    }

    return (
      <div className={classes.root}>
        <div className={classes.inCardContainer}>
          <Typography className={classes.layoutColor} gutterBottom variant="h6">
            Auto Arrange
          </Typography>
          <Divider />
          <Typography variant="body1">Uncheck to disable auto-arranging layout for each model.</Typography>
          <FormGroup column>
            {LAYOUT_TREE_TYPES.map(treeType => (
              <FormControlLabel
                key={treeType}
                label={LAYOUT_LABELS[treeType]}
                control={(
                  <LayoutSwitch
                    checked={layoutConfig[treeType]}
                    onChange={() => this.handleLayoutChange(treeType)}
                  />
                )}
              />
            ))}
          </FormGroup>
        </div>
        <br />
        <div className={classes.inCardContainer}>
          <Typography
            className={classes.transferColor}
            gutterBottom
            variant="h6"
          >
            {intl.formatMessage({ id: 'advancedSettings.transferModel' })}
          </Typography>
          <Divider />
          <Typography variant="body1">
            {intl.formatMessage({ id: 'advancedSettings.transferModelDesc' })}
          </Typography>
          <Typography variant="body1" paragraph>
            {intl.formatMessage({ id: 'advancedSettings.transferModelDesc2' })}
          </Typography>
          <TransferButton
            disabled
            variant="outlined"
            size="large"
          >
            {intl.formatMessage({ id: 'transfer' })}
          </TransferButton>
        </div>
        <br />
        <div className={classes.inCardContainer}>
          <Typography
            className={classes.removeColor}
            gutterBottom
            variant="h6"
          >
            {intl.formatMessage({ id: 'advancedSettings.removeModel' })}
          </Typography>
          <Divider />
          <Typography variant="body1">
            {intl.formatMessage({ id: 'advancedSettings.removeModelDesc' })}
          </Typography>
          <Typography variant="body1" paragraph>
            <b>{intl.formatMessage({ id: 'advancedSettings.removeModelDesc2' })}</b>
          </Typography>
          <RemoveButton
            onClick={e => this.handleToggle(e, 'openTypedConfirmationDialog')}
            variant="outlined"
            size="large"
          >
            {intl.formatMessage({ id: 'remove' })}
          </RemoveButton>
          <TypedConfirmationDialog
            title={intl.formatMessage({ id: 'advancedSettings.confirmationRequired' })}
            content={(
              <div>
                <Typography
                  className={classes.removeColor}
                  variant="h6"
                  gutterBottom
                >
                  {intl.formatMessage({ id: 'advancedSettings.confirmationRequiredDesc' })}
                </Typography>
                <Typography variant="body1">
                  {intl.formatMessage({ id: 'advancedSettings.confirmationRequiredDesc2' })}
                </Typography>
                <Typography variant="body1">
                  {intl.formatMessage({ id: 'advancedSettings.confirmationRequiredDesc3' })}
                </Typography>
                <Typography variant="body1">
                  {intl.formatMessage({ id: 'advancedSettings.confirmationRequiredDesc4' })}
                </Typography>
              </div>
            )}
            open={openTypedConfirmationDialog}
            onConfirmed={() => this.handleOnConfirmed()}
            onRequestClose={e => this.handleToggle(e, 'openTypedConfirmationDialog')}
            matchText={title}
          />
        </div>
      </div>
    );
  }
}

ModelSettingsAdvanced.propTypes = {
  details: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    date_created: PropTypes.string.isRequired,
    date_modified: PropTypes.string.isRequired,
    creator: PropTypes.number.isRequired,
    assigned_users: PropTypes.arrayOf(Number).isRequired,
    subsystems: PropTypes.arrayOf(Number).isRequired,
  }).isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
};

export default withStyles(styles)(injectIntl(ModelSettingsAdvanced));
